/**
 * Windows Hello / Touch ID over WebAuthn, used two ways depending on what the
 * browser can actually do. The difference matters, so the UI states it plainly
 * rather than implying protection that is not there.
 *
 *   prf   — the authenticator derives a secret we never see, and the owner key
 *           is encrypted with it at rest. Without a successful biometric check
 *           the stored key is ciphertext and nothing can use it. This is real
 *           protection.
 *
 *   gate  — the browser has no PRF extension, so we can only *ask* for a
 *           biometric check before proceeding. It puts a human in the loop and
 *           stops someone walking up to an unlocked laptop, but it is a
 *           ceremony: the key sits in IndexedDB either way.
 */
import { base64UrlToBytes, bytesToBase64Url } from '../../shared/base64.ts';
import { WEBAUTHN_RP_NAME } from '../../shared/constants.ts';

export type Protection = 'none' | 'webauthn-prf' | 'webauthn-gate';

export interface Enrollment {
  protection: Exclude<Protection, 'none'>;
  credentialId: string;
  salt: string;
}

interface PrfResults {
  prf?: { enabled?: boolean; results?: { first?: ArrayBuffer } };
}

export function webauthnSupported(): boolean {
  return typeof window !== 'undefined' && 'PublicKeyCredential' in window;
}

/** Is there a built-in authenticator (Hello, Touch ID) on this device? */
export async function platformAuthenticatorAvailable(): Promise<boolean> {
  if (!webauthnSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/** ArrayBuffer-backed on purpose: WebAuthn and WebCrypto both refuse a view
 *  over the generic ArrayBufferLike that the DOM types default to. */
function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(new ArrayBuffer(length));
  crypto.getRandomValues(bytes);
  return bytes;
}

/**
 * Registers a platform authenticator for this key and reports which of the two
 * protection levels we actually got.
 */
export async function enroll(kid: string, owner: string): Promise<Enrollment> {
  const salt = randomBytes(32);

  const created = (await navigator.credentials.create({
    publicKey: {
      challenge: randomBytes(32),
      rp: { name: WEBAUTHN_RP_NAME, id: location.hostname },
      user: { id: randomBytes(16), name: owner, displayName: `${owner} (${kid})` },
      // ES256 first, RS256 as a fallback for older authenticators.
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        residentKey: 'preferred',
        userVerification: 'required',
      },
      timeout: 120_000,
      attestation: 'none',
      extensions: { prf: {} } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;

  if (!created) throw new Error('Устройство не подтвердило регистрацию.');

  const results = created.getClientExtensionResults() as PrfResults;
  const credentialId = bytesToBase64Url(new Uint8Array(created.rawId));

  return {
    protection: results.prf?.enabled ? 'webauthn-prf' : 'webauthn-gate',
    credentialId,
    salt: bytesToBase64Url(salt),
  };
}

/**
 * Asks for the biometric check. Returns the PRF secret when the authenticator
 * supports it, or null when this is only a gate.
 */
export async function verify(enrollment: Enrollment): Promise<Uint8Array<ArrayBuffer> | null> {
  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: randomBytes(32),
      rpId: location.hostname,
      allowCredentials: [
        { type: 'public-key', id: base64UrlToBytes(enrollment.credentialId) },
      ],
      userVerification: 'required',
      timeout: 120_000,
      extensions: {
        prf: { eval: { first: base64UrlToBytes(enrollment.salt) } },
      } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;

  if (!assertion) throw new Error('Проверка не пройдена.');

  const results = assertion.getClientExtensionResults() as PrfResults;
  const first = results.prf?.results?.first;
  if (!first) {
    if (enrollment.protection === 'webauthn-prf') {
      // The key is encrypted with a secret this authenticator suddenly will not
      // produce. Failing loudly beats silently pretending the key is gone.
      throw new Error('Аутентификатор не выдал ключ шифрования. Попробуйте ещё раз.');
    }
    return null;
  }
  return new Uint8Array(first) as Uint8Array<ArrayBuffer>;
}

/** Turns the PRF secret into an AES-GCM key. */
export async function keyFromSecret(secret: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  const material = await crypto.subtle.digest('SHA-256', secret);
  return crypto.subtle.importKey('raw', material, { name: 'AES-GCM' }, false, [
    'encrypt', 'decrypt',
  ]);
}
